import { useMemo } from 'react';
import PropTypes from 'prop-types';

import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { calcularSegmentosSms } from 'src/utils/smsSegmentos';

import { formatDataLocal } from '../useCampanhaForm';
import { CAMPANHAS_DESIGN as D } from '../../Campanhas.design';

// O agendador do backend (createCampaignJobs) so distribui entre 08:00 e 17:59 em
// America/Sao_Paulo, um por minuto. Oferecer horarios fora disso na UI seria mentir.
const HORAS = Array.from({ length: 10 }, (_, i) => i + 8);
const MINUTOS = ['00', '10', '20', '30', '40', '50'];
const ULTIMA_HORA = 17;

export default function PassoAgendamento({ form, set, estimativa }) {
    const ehSms = form.tipoEnvio === 'sms';
    const metricas = useMemo(() => calcularSegmentosSms(form.body), [form.body]);

    const agora = new Date();
    const hoje = formatDataLocal(agora);
    const horaAtual = agora.getHours();
    // Depois das 18h não há slot válido hoje — min do date picker vira amanhã.
    const dataMinima =
        horaAtual > ULTIMA_HORA
            ? formatDataLocal(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1))
            : hoje;
    const ehHoje = form.dataCampanha === hoje;

    // Parse local (YYYY-MM-DD) — new Date('YYYY-MM-DD') é UTC e pode mudar o dia em BRT.
    const ehFimDeSemana = useMemo(() => {
        if (!form.dataCampanha) return false;
        const [y, m, d] = form.dataCampanha.split('-').map(Number);
        if (!y || !m || !d) return false;
        const diaSemana = new Date(y, m - 1, d).getDay();
        return diaSemana === 0 || diaSemana === 6;
    }, [form.dataCampanha]);

    const total = Number(estimativa) || 0;
    // 1 disparo por minuto: a duracao e previsivel e o usuario precisa saber que
    // uma campanha de 500 numeros nao termina em 5 minutos.
    const duracaoMin = total;
    const duracaoTexto =
        duracaoMin < 60
            ? `${duracaoMin} min`
            : `${Math.floor(duracaoMin / 60)}h${String(duracaoMin % 60).padStart(2, '0')}`;

    return (
        <div className="space-y-4">
            <div className={D.wizard.secao}>
                <p className={D.wizard.secaoTitulo}>Quando enviar?</p>
                <p className={D.wizard.secaoSub}>Os disparos acontecem entre 08h e 18h, um por minuto.</p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div>
                        <Label className={D.wizard.label}>Data</Label>
                        <Input
                            type="date"
                            min={dataMinima}
                            value={form.dataCampanha}
                            onChange={(e) => set('dataCampanha', e.target.value)}
                            className={D.wizard.input}
                        />
                    </div>
                    <div>
                        <Label className={D.wizard.label}>Hora</Label>
                        <Select value={form.horaCampanha} onValueChange={(v) => set('horaCampanha', v)}>
                            <SelectTrigger className={D.wizard.input}>
                                <SelectValue placeholder="--" />
                            </SelectTrigger>
                            <SelectContent>
                                {HORAS.map((h) => (
                                    <SelectItem
                                        key={h}
                                        value={String(h).padStart(2, '0')}
                                        disabled={ehHoje && h < horaAtual}
                                    >
                                        {`${String(h).padStart(2, '0')}h`}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className={D.wizard.label}>Minuto</Label>
                        <Select value={form.minutoCampanha} onValueChange={(v) => set('minutoCampanha', v)}>
                            <SelectTrigger className={D.wizard.input}>
                                <SelectValue placeholder="--" />
                            </SelectTrigger>
                            <SelectContent>
                                {MINUTOS.map((m) => (
                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className={`${D.wizard.avisoInfo} mt-4`}>
                    <Iconify icon="mdi:information-outline" width={14} className="flex-shrink-0 mt-px" />
                    <span>
                        O que não couber até as 18h continua automaticamente no dia seguinte a partir das 08h.
                    </span>
                </div>

                {ehFimDeSemana && !ehSms && (
                    <div className={`${D.wizard.avisoAlerta} mt-3`}>
                        <Iconify icon="mdi:alert-outline" width={14} className="flex-shrink-0 mt-px" />
                        <span>
                            Você escolheu um fim de semana. Envios fora do horário comercial podem gerar
                            restrições e reduzir a qualidade do número na Meta.
                        </span>
                    </div>
                )}
            </div>

            <div className={D.wizard.secao}>
                <p className={D.wizard.secaoTitulo}>Resumo</p>

                <div className="mt-3 space-y-2">
                    <Linha label="Campanha" valor={form.nome || '—'} />
                    <Linha label="Canal" valor={ehSms ? 'SMS' : 'WhatsApp'} />
                    <Linha
                        label="Destinatários"
                        valor={total ? `${total}` : 'a calcular no envio'}
                    />
                    {total > 0 && <Linha label="Duração estimada" valor={duracaoTexto} />}
                    {ehSms && total > 0 && (
                        <Linha
                            label="Créditos"
                            valor={`≈ ${total * metricas.segmentos} (${metricas.segmentos} por destinatário)`}
                            destaque
                        />
                    )}
                    <Linha
                        label="Início"
                        valor={
                            form.horaCampanha
                                ? `${form.dataCampanha.split('-').reverse().join('/')} às ${form.horaCampanha}:${form.minutoCampanha}`
                                : '—'
                        }
                    />
                </div>
            </div>
        </div>
    );
}

function Linha({ label, valor, destaque }) {
    return (
        <div className="flex items-start justify-between gap-3 py-1">
            <span className="text-[10px] text-gray-500 dark:text-[#ABABAD] uppercase tracking-wider flex-shrink-0 pt-0.5">
                {label}
            </span>
            <span
                className={`text-[11px] text-right tabular-nums ${
                    destaque
                        ? 'font-bold text-[#7C3AED]'
                        : 'text-gray-900 dark:text-[#D1D2D3]'
                }`}
            >
                {valor}
            </span>
        </div>
    );
}

Linha.propTypes = {
    label: PropTypes.string.isRequired,
    valor: PropTypes.node,
    destaque: PropTypes.bool,
};

PassoAgendamento.propTypes = {
    form: PropTypes.object.isRequired,
    set: PropTypes.func.isRequired,
    estimativa: PropTypes.number,
};
